<template>
  <div :class="['flex items-center', noBorder ? '' : 'border-b border-gray-200']">
    <component
      :is="tab.to ? 'router-link' : 'button'"
      v-for="tab in normalizedTabs"
      :key="tab.id || tab.label"
      :to="tab.to || undefined"
      :type="tab.to ? undefined : 'button'"
      :aria-label="tab.description ? `${tab.label}: ${tab.description}` : undefined"
      style="font-size: 13px;"
      :class="[
        'px-3 py-2.5 font-medium border-b-2 -mb-px transition-colors no-underline cursor-pointer bg-transparent',
        isActive(tab)
          ? 'border-gray-900 text-gray-900'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      ]"
      @click="switchTab(tab)"
      @mouseenter="showTooltip(tab, $event)"
      @mouseleave="hideTooltip"
      @focus="showTooltip(tab, $event)"
      @blur="hideTooltip"
    >
      <font-awesome-icon v-if="tab.icon" :icon="tab.icon" class="mr-1 text-xs" />
      {{ tab.label }}
    </component>
  </div>
  <Teleport to="body">
    <div
      v-if="tooltip"
      role="tooltip"
      class="pointer-events-none fixed z-50 w-72 max-w-[calc(100vw-1rem)] rounded bg-gray-900 px-2.5 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg"
      :style="tooltip.style"
    >
      {{ tooltip.description }}
    </div>
  </Teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Page_Tabs',
  components: {
    FontAwesomeIcon,
  },
  props: {
    initialTab: {
      type: String,
      default: ''
    },
    // Each item: string | { label, id?, icon?, description?, to? }
    // When `to` is provided, the tab renders as a router-link
    // When `id` is provided, it is used for matching and emitting instead of label
    // `description` is exposed as an immediate hover/focus tooltip.
    tabs: {
      type: Array,
      required: true
    },
    noBorder: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tooltip: null,
    };
  },
  computed: {
    currentTab() {
      return this.initialTab;
    },
    normalizedTabs() {
      return this.tabs.map(tab =>
        typeof tab === 'string' ? { label: tab } : tab
      );
    },
  },
  methods: {
    isActive(tab) {
      if (tab.to) {
        return this.$route.path === tab.to || this.$route.path.startsWith(tab.to + '/');
      }
      return this.currentTab === (tab.id || tab.label);
    },
    switchTab(tab) {
      this.$emit('select', tab.id || tab.label);
    },
    showTooltip(tab, event) {
      if (!tab.description) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const tooltipWidth = 288;
      const edgePadding = 8;
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, tooltipWidth / 2 + edgePadding),
        window.innerWidth - tooltipWidth / 2 - edgePadding,
      );
      this.tooltip = {
        description: tab.description,
        style: {
          top: `${rect.bottom + 8}px`,
          left: `${left}px`,
          transform: 'translateX(-50%)',
        },
      };
    },
    hideTooltip() {
      this.tooltip = null;
    }
  }
}
</script>

<style scoped>
a.no-underline {
  text-decoration: none;
  color: inherit;
}

a.no-underline:hover {
  color: inherit;
}

</style>
